import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { TrendingUp, Send, CheckCircle, Clock } from 'lucide-react';

export default function VendorBiddingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openRFPs, setOpenRFPs] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [selectedRFP, setSelectedRFP] = useState(null);
  const [bidData, setBidData] = useState({
    property_id: '',
    quoted_amount: '',
    breakdown: { venue_rental: '', catering: '', equipment: '', service_charge: '' },
    inclusions: '',
    exclusions: '',
    payment_terms: '50% advance, 50% on completion',
    validity_days: 15,
    special_notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rfpsRes, bidsRes, propsRes] = await Promise.all([
        api.getRFPs(),
        api.getVendorBids(),
        api.getVendorProperties()
      ]);
      // Filter open RFPs where vendor hasn't bid yet
      const bidRFPIds = bidsRes.data.map(b => b.rfp_id);
      const open = rfpsRes.data.filter(rfp => rfp.status === 'open' && !bidRFPIds.includes(rfp.id));
      setOpenRFPs(open);
      setMyBids(bidsRes.data);
      setProperties(propsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBidDialog = (rfp) => {
    setSelectedRFP(rfp);
    setShowBidDialog(true);
  };

  const handleSubmitBid = async (e) => {
    e.preventDefault();
    
    try {
      const breakdown = {};
      Object.entries(bidData.breakdown).forEach(([key, value]) => {
        if (value) breakdown[key] = parseFloat(value);
      });

      await api.createBid({
        rfp_id: selectedRFP.id,
        property_id: bidData.property_id,
        quoted_amount: parseFloat(bidData.quoted_amount),
        breakdown,
        inclusions: bidData.inclusions.split('\n').filter(Boolean),
        exclusions: bidData.exclusions.split('\n').filter(Boolean),
        payment_terms: bidData.payment_terms,
        validity_days: parseInt(bidData.validity_days),
        special_notes: bidData.special_notes
      });
      
      toast.success('Bid submitted successfully!');
      setShowBidDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit bid');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      submitted: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Submitted' },
      shortlisted: { color: 'bg-purple-100 text-purple-800', icon: CheckCircle, label: 'Shortlisted!' },
      negotiating: { color: 'bg-yellow-100 text-yellow-800', icon: TrendingUp, label: 'Negotiating' },
      pending_approval: { color: 'bg-orange-100 text-orange-800', icon: Clock, label: 'Pending Approval' },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Won!' },
      rejected: { color: 'bg-red-100 text-red-800', icon: null, label: 'Not Selected' }
    };
    const { color, icon: Icon, label } = config[status] || config.submitted;
    return (
      <Badge className={color}>
        {Icon && <Icon className="w-3 h-3 mr-1" />}
        {label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600 mx-auto mb-4"></div>
          <p className="text-slate-800">Loading opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-navy-800">RFP Bidding</h1>
          <p className="text-slate-800 mt-2 text-sm sm:text-base">Bid on open RFPs and manage your submissions</p>
        </div>

        {/* My Bids Section */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-navy-800 mb-4">My Bids ({myBids.length})</h2>
          {myBids.length === 0 ? (
            <Card className="border-2 border-dashed border-navy-300">
              <CardContent className="py-8 text-center text-slate-700">
                You haven't submitted any bids yet. Check open RFPs below!
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {myBids.map(bid => (
                <Card key={bid.id} className="border-2 border-navy-200">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-navy-800">{bid.property_name}</h3>
                          {getStatusBadge(bid.status)}
                        </div>
                        <p className="text-sm text-slate-800">
                          Quote: ₹{bid.quoted_amount.toLocaleString()} | Submitted: {new Date(bid.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {bid.status === 'negotiating' && (
                        <Button
                          onClick={() => navigate(`/vendor/bids/${bid.id}/negotiate`)}
                          className="bg-yellow-600 hover:bg-yellow-700"
                          size="sm"
                        >
                          View Chat
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Open RFPs Section */}
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-navy-800 mb-4">Open RFPs ({openRFPs.length})</h2>
          {openRFPs.length === 0 ? (
            <Card className="border-2 border-dashed border-navy-300">
              <CardContent className="py-16 text-center">
                <TrendingUp className="h-16 w-16 text-navy-300 mx-auto mb-4" />
                <p className="text-slate-800">No open RFPs available right now. Check back soon!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6">
              {openRFPs.map(rfp => (
                <Card key={rfp.id} className="border-2 border-navy-200 hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
                      <div className="flex-1">
                        <CardTitle className="text-lg sm:text-xl text-navy-800">{rfp.event_name}</CardTitle>
                        <div className="flex flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm text-slate-800 mt-2">
                          <span>🎯 {rfp.event_type}</span>
                          <span>📍 {rfp.location}</span>
                          <span>👥 {rfp.attendees} attendees</span>
                          <span className="hidden sm:inline">📅 {new Date(rfp.start_date).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800 text-xs sm:text-sm">
                        ₹ {rfp.budget_range}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <div className="text-sm font-medium text-slate-900 mb-1">Requirements:</div>
                      <p className="text-sm text-slate-800 line-clamp-3">{rfp.requirements}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-700">
                        {rfp.quotes_received} bids received
                      </div>
                      <Button
                        onClick={() => handleOpenBidDialog(rfp)}
                        className="bg-navy-600 hover:bg-navy-700"
                      >
                        <Send className="mr-2 h-4 w-4" />
                        Submit Bid
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bid Submission Dialog */}
      <Dialog open={showBidDialog} onOpenChange={setShowBidDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submit Bid for {selectedRFP?.event_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitBid} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Select Property *</label>
              <Select
                value={bidData.property_id}
                onValueChange={(value) => setBidData({...bidData, property_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(prop => (
                    <SelectItem key={prop.id} value={prop.id}>
                      {prop.name} - {prop.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Total Quote Amount *</label>
              <Input
                required
                type="number"
                placeholder="e.g., 250000"
                value={bidData.quoted_amount}
                onChange={(e) => setBidData({...bidData, quoted_amount: e.target.value})}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Inclusions * (one per line)</label>
              <Textarea
                required
                rows={4}
                placeholder="WiFi
Parking
Basic AV Equipment
Mineral Water"
                value={bidData.inclusions}
                onChange={(e) => setBidData({...bidData, inclusions: e.target.value})}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Exclusions (one per line)</label>
              <Textarea
                rows={3}
                placeholder="Decorations
Entertainment
Transportation"
                value={bidData.exclusions}
                onChange={(e) => setBidData({...bidData, exclusions: e.target.value})}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Payment Terms *</label>
              <Input
                required
                placeholder="e.g., 50% advance, 50% on completion"
                value={bidData.payment_terms}
                onChange={(e) => setBidData({...bidData, payment_terms: e.target.value})}
              />
            </div>

            <Button type="submit" className="w-full bg-navy-600 hover:bg-navy-700">
              <Send className="mr-2 h-4 w-4" />
              Submit Bid
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
